import { useCallback, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

type SelectableOption = {
  id: string;
  icon?: React.ReactNode;
  title: string;
  description?: string;
};

type SelectedItem = {
  id: string;
  sourceId: string;
};

type SelectableCardGridProps<
  TOption extends SelectableOption,
  TItem extends SelectedItem,
> = {
  options: TOption[];
  selectedIds: string[];
  disabledIds?: string[];
  onSelectionChange: (ids: string[]) => void;
  items: TItem[];
  onReorder: (items: TItem[]) => void;
  renderItem: (
    item: TItem,
    index: number,
    onRemove: (id: string) => void,
  ) => React.ReactNode;
  renderOptionBadge?: (option: TOption) => React.ReactNode;
  sectionTitle?: string;
  sectionSubtitle?: string;
  itemsTitle?: string;
  itemsSubtitle?: string;
  emptyIcon?: React.ReactNode;
  emptyTitle?: string;
  emptySubtitle?: string;
};

export function SelectableCardGrid<
  TOption extends SelectableOption,
  TItem extends SelectedItem,
>({
  options,
  selectedIds,
  disabledIds,
  onSelectionChange,
  items,
  onReorder,
  renderItem,
  renderOptionBadge,
  sectionTitle = 'Select sources',
  sectionSubtitle,
  itemsTitle = 'Selected items',
  itemsSubtitle,
  emptyIcon,
  emptyTitle = 'No items selected',
  emptySubtitle = 'Select one or more options above to get started.',
}: SelectableCardGridProps<TOption, TItem>) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const handleActiveRef = useRef<number | null>(null);
  const dragNodeRef = useRef<HTMLDivElement | null>(null);

  const handleToggle = useCallback(
    (id: string) => {
      if (disabledIds?.includes(id)) return;
      const isSelected = selectedIds.includes(id);
      if (isSelected) {
        onSelectionChange(selectedIds.filter((s) => s !== id));
      } else {
        onSelectionChange([...selectedIds, id]);
      }
    },
    [disabledIds, selectedIds, onSelectionChange],
  );

  const emptyDragImage = useRef<HTMLDivElement | null>(null);

  const handleDragStart = useCallback(
    (e: React.DragEvent<HTMLDivElement>, index: number) => {
      if (handleActiveRef.current !== index) {
        e.preventDefault();
        return;
      }
      setDragIndex(index);
      dragNodeRef.current = e.currentTarget;
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(index));

      if (emptyDragImage.current) {
        e.dataTransfer.setDragImage(emptyDragImage.current, 0, 0);
      }

      requestAnimationFrame(() => {
        if (dragNodeRef.current) {
          dragNodeRef.current.style.opacity = '0.4';
        }
      });
    },
    [],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>, index: number) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (dragIndex !== index) {
        setDragOverIndex(index);
      }
    },
    [dragIndex],
  );

  const handleDragEnd = useCallback(() => {
    if (dragNodeRef.current) {
      dragNodeRef.current.style.opacity = '1';
    }
    setDragIndex(null);
    setDragOverIndex(null);
    handleActiveRef.current = null;
    dragNodeRef.current = null;
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>, dropIndex: number) => {
      e.preventDefault();
      if (dragIndex === null || dragIndex === dropIndex) {
        handleDragEnd();
        return;
      }

      const reordered = [...items];
      const [moved] = reordered.splice(dragIndex, 1);
      reordered.splice(dropIndex, 0, moved);
      onReorder(reordered);
      handleDragEnd();
    },
    [dragIndex, items, onReorder, handleDragEnd],
  );

  return (
    <div className="flex flex-col gap-6">
      <div
        ref={emptyDragImage}
        className="fixed -left-[9999px] size-0"
        aria-hidden="true"
      />
      <div>
        <h2 className="text-base font-semibold text-gray-900">
          {sectionTitle}
          {sectionSubtitle && (
            <span className="ml-2 text-sm font-normal text-gray-500">
              — {sectionSubtitle}
            </span>
          )}
        </h2>
        <div className="mt-3 flex gap-3 overflow-x-auto pb-2">
          {options.map((option) => {
            const isSelected = selectedIds.includes(option.id);
            const isDisabled = disabledIds?.includes(option.id) ?? false;
            return (
              <button
                key={option.id}
                type="button"
                disabled={isDisabled}
                aria-disabled={isDisabled}
                onClick={() => handleToggle(option.id)}
                className={cn(
                  'relative flex min-w-[200px] flex-1 flex-col gap-2 rounded-lg border-2 p-4 text-left transition-all',
                  isSelected
                    ? 'border-blue-500 bg-blue-50/50'
                    : 'border-gray-200 bg-white hover:border-gray-300',
                  isDisabled &&
                    'cursor-not-allowed border-gray-200 bg-gray-50 opacity-60 hover:border-gray-200',
                )}
              >
                <div className="flex items-start justify-between">
                  {option.icon && (
                    <span className="text-xl">{option.icon}</span>
                  )}
                  <div
                    className={cn(
                      'flex size-5 items-center justify-center rounded-full border-2 transition-colors',
                      isSelected
                        ? 'border-blue-500 bg-blue-500'
                        : 'border-gray-300',
                    )}
                  >
                    {isSelected && (
                      <svg
                        className="size-3 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={3}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </div>
                </div>
                <div className="font-medium text-gray-900">{option.title}</div>
                {option.description && (
                  <div className="text-xs text-gray-500">
                    {option.description}
                  </div>
                )}
                {renderOptionBadge?.(option)}
              </button>
            );
          })}
        </div>
      </div>

      {items.length > 0 ? (
        <div>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-base font-semibold text-gray-900">
              {itemsTitle}
              {itemsSubtitle && (
                <span className="ml-2 text-sm font-normal text-gray-500">
                  — {itemsSubtitle}
                </span>
              )}
            </h2>
            <span className="text-sm text-gray-500">
              {selectedIds.length} items selected
            </span>
          </div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {items.map((item, index) => (
              <div
                key={item.id}
                draggable
                onMouseDown={(e) => {
                  if ((e.target as HTMLElement).closest('[data-drag-handle]')) {
                    handleActiveRef.current = index;
                  } else {
                    handleActiveRef.current = null;
                  }
                }}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                onDrop={(e) => handleDrop(e, index)}
                className={cn(
                  'rounded-lg border transition-all',
                  dragOverIndex === index && dragIndex !== index
                    ? 'border-blue-400 ring-2 ring-blue-200'
                    : 'border-gray-200',
                  dragIndex === index && 'opacity-50',
                )}
              >
                {renderItem(item, index, handleToggle)}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50/50 py-16">
          {emptyIcon && (
            <div className="mb-3 text-3xl text-gray-300">{emptyIcon}</div>
          )}
          <p className="text-sm font-medium text-gray-500">{emptyTitle}</p>
          <p className="mt-1 text-xs text-gray-400">{emptySubtitle}</p>
        </div>
      )}
    </div>
  );
}
